import { useEffect, useState } from "react";

/**
 * Active power-ups HUD.
 *
 * Two flavours, both extracted from `GameCanvas.jsx` on 2026-05-13:
 *
 *   <ActivePowerupsHud />     — floating strip while playing
 *   <PauseActivePowerups />   — verbose list inside the pause overlay
 *
 * Both expect `powerups` to be the same `activePowerups` array used by
 * the canvas: `[{ kind, startedAt, until, duration }, ...]`. The `info`
 * lookup (`POWERUP_INFO`) is passed in so GameCanvas remains the single
 * source of truth for power-up palettes/labels.
 *
 * The bars need to repaint every animation frame so the depletion fills
 * smoothly. We drive that via a local 16ms RAF tick instead of pushing
 * `hudTick` through props — keeps GameCanvas one level lighter and
 * isolates the repaint cost to whichever variant is mounted.
 */
function useAnimationFrameTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    let raf = 0;
    let alive = true;
    const loop = () => {
      if (!alive) return;
      setTick((t) => (t + 1) % 1_000_000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, []);
}

export function ActivePowerupsHud({ powerups, info, isFullscreen, hidden }) {
  useAnimationFrameTick();
  if (hidden || !powerups || powerups.length === 0) return null;
  return (
    <div
      className={`absolute flex gap-2 pointer-events-none ${
        isFullscreen
          ? "right-3 top-1/2 -translate-y-1/2 flex-col items-end"
          : "top-3 left-3"
      }`}
      data-testid="active-powerups"
    >
      {powerups.slice(-4).map((p, i) => {
        const meta = info[p.kind] || { color: "#ffffff", label: p.kind };
        const now = performance.now();
        const progress = Math.max(
          0,
          Math.min(1, (p.until - now) / (p.duration || 1)),
        );
        return (
          <div
            key={`${p.startedAt}-${i}`}
            className="relative px-2 py-1 border bg-black/70 font-mono-cosmic text-[10px] tracking-widest uppercase"
            style={{
              borderColor: `${meta.color}aa`,
              color: meta.color,
              boxShadow: `0 0 10px ${meta.color}55`,
            }}
            data-testid={`active-powerup-${p.kind}`}
          >
            <div className="pb-[3px]">{meta.label || p.kind}</div>
            <div
              className="absolute left-0 bottom-0 h-[3px]"
              style={{
                width: `${progress * 100}%`,
                background: meta.color,
                boxShadow: `0 0 6px ${meta.color}`,
                transition: "width 150ms linear",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

export function PauseActivePowerups({ powerups, info }) {
  useAnimationFrameTick();
  if (!powerups || powerups.length === 0) return null;
  return (
    <div
      className="w-full max-w-md mb-5"
      data-testid="pause-active-powerups"
    >
      <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-2 text-left">
        Active Power-ups
      </div>
      <div className="flex flex-col gap-1.5">
        {powerups.map((p, i) => {
          const meta = info[p.kind] || { color: "#fff", label: p.kind };
          const remaining = Math.max(0, p.until - performance.now());
          const pct = Math.max(0, Math.min(1, remaining / (p.duration || 1)));
          return (
            <div
              key={`${p.startedAt}-${i}`}
              className="flex items-center gap-3"
              data-testid={`pause-active-${p.kind}`}
            >
              <span
                className="font-mono-cosmic text-[10px] tracking-widest uppercase w-20 text-left"
                style={{ color: meta.color }}
              >
                {meta.label}
              </span>
              <div className="flex-1 relative h-1 bg-white/10 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0"
                  style={{ width: `${pct * 100}%`, background: meta.color }}
                />
              </div>
              <span className="font-mono-cosmic text-[10px] tabular-nums text-white/60 w-10 text-right">
                {(remaining / 1000).toFixed(1)}s
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
